import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { useVendorAuth } from "../VendorAuthContext";

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR LOGIN PAGE — Premium SaaS style (DCC gold + navy)
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  navy: "#0f172a",
  navyLight: "#1e293b",
  gold: "#d4a373",
  goldHover: "#c49363",
  cream: "#f8f5f0",
  white: "#ffffff",
  slate: "#64748b",
  slateLt: "#94a3b8",
  line: "#e2e8f0",
  red: "#ef4444",
};

const VendorLogin: React.FC = () => {
  const { login } = useVendorAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err?.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        bgcolor: T.navy,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.03,
          backgroundImage: `radial-gradient(${T.gold} 1px, transparent 1px)`,
          backgroundSize: "32px 32px",
          pointerEvents: "none",
        }}
      />

      {/* Left branding panel (hidden on mobile) */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: 1,
          flexDirection: "column",
          justifyContent: "center",
          px: 8,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "14px",
            bgcolor: "rgba(212,163,115,0.12)",
            border: `1px solid rgba(212,163,115,0.2)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 4,
          }}
        >
          <StorefrontOutlinedIcon sx={{ fontSize: 28, color: T.gold }} />
        </Box>

        <Typography
          sx={{
            fontSize: 42,
            fontWeight: 800,
            color: T.white,
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            mb: 2,
          }}
        >
          Durham Convention
          <br />
          Centre
        </Typography>

        <Typography
          sx={{
            fontSize: 17,
            color: T.slateLt,
            lineHeight: 1.6,
            maxWidth: 420,
          }}
        >
          Manage your services, track events, update pricing, and monitor
          revenue — all from one place.
        </Typography>

        {/* Decorative cards */}
        <Box sx={{ display: "flex", gap: 2, mt: 5 }}>
          {["Events", "Pricing", "Revenue"].map((label) => (
            <Box
              key={label}
              sx={{
                px: 2.5,
                py: 1.5,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
                backdropFilter: "blur(8px)",
              }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 600, color: T.slateLt }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right login form */}
      <Box
        sx={{
          width: { xs: "100%", md: 520 },
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: T.white,
          borderRadius: { xs: 0, md: "32px 0 0 32px" },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ width: "100%", maxWidth: 380, px: { xs: 3, sm: 0 } }}
        >
          {/* Mobile logo */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1.5,
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: T.navy,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <StorefrontOutlinedIcon sx={{ fontSize: 20, color: T.gold }} />
            </Box>
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, color: T.navy }}
            >
              DCC Vendor Portal
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 800,
              color: T.navy,
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            Welcome back
          </Typography>
          <Typography sx={{ fontSize: 14, color: T.slate, mb: 4 }}>
            Sign in to your vendor account
          </Typography>

          {/* Error message */}
          {error && (
            <Box
              sx={{
                mb: 2.5,
                px: 2,
                py: 1.5,
                borderRadius: "10px",
                bgcolor: "#fef2f2",
                border: "1px solid #fecaca",
              }}
            >
              <Typography sx={{ fontSize: 13, color: T.red, fontWeight: 500 }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* Email field */}
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: T.navy, mb: 0.8 }}
          >
            Email
          </Typography>
          <TextField
            fullWidth
            placeholder="vendor@example.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            sx={{
              mb: 2.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: 14,
                bgcolor: "#f8fafc",
                "& fieldset": { borderColor: T.line },
                "&:hover fieldset": { borderColor: T.slateLt },
                "&.Mui-focused fieldset": {
                  borderColor: T.gold,
                  borderWidth: 1.5,
                },
              },
            }}
          />

          {/* Password field */}
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: T.navy, mb: 0.8 }}
          >
            Password
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter your password"
            type={showPw ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPw(!showPw)}
                    edge="end"
                    size="small"
                  >
                    {showPw ? (
                      <VisibilityOffOutlinedIcon
                        sx={{ fontSize: 18, color: T.slateLt }}
                      />
                    ) : (
                      <VisibilityOutlinedIcon
                        sx={{ fontSize: 18, color: T.slateLt }}
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: 14,
                bgcolor: "#f8fafc",
                "& fieldset": { borderColor: T.line },
                "&:hover fieldset": { borderColor: T.slateLt },
                "&.Mui-focused fieldset": {
                  borderColor: T.gold,
                  borderWidth: 1.5,
                },
              },
            }}
          />

          {/* Login button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.5,
              borderRadius: "10px",
              fontSize: 14,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: T.navy,
              color: T.white,
              boxShadow: "0 4px 14px rgba(15,23,42,0.25)",
              "&:hover": {
                bgcolor: T.navyLight,
                boxShadow: "0 6px 20px rgba(15,23,42,0.35)",
              },
              "&.Mui-disabled": {
                bgcolor: T.navyLight,
                color: "rgba(255,255,255,0.6)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: T.white }} />
            ) : (
              "Sign in"
            )}
          </Button>

          {/* Footer */}
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Typography sx={{ fontSize: 12, color: T.slateLt }}>
              Durham Convention Centre &middot; Vendor Portal
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VendorLogin;
